/**
 * R-15 D2 (T-111, #876) — the Feedback and Help pill: on every panel (page heads and the record presenter), a quiet "⋯"
 * that opens Report a problem / Give feedback / Ask for help. The kit owns the control and its marker; the shell provides
 * what it does (FeedbackProvider): whether the module is installed, and the modal that captures the context and sends it.
 * Without a provider, or with the module absent, the pill is not there at all.
 *
 * R-16 T-125: the pill reads where it is the moment its menu OPENS — the address bar, the page's declared screen and the
 * panel it sits on — and shows the About line it will record before anyone submits; the form sends what was read then.
 */
import * as React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from './Button';
import { Menu, MenuItem, MenuText } from './Menu';

export type FeedbackKind = 'problem' | 'feedback' | 'help';

export const FEEDBACK_KINDS: readonly { kind: FeedbackKind; label: string }[] = [
  { kind: 'problem', label: 'Report a problem' },
  { kind: 'feedback', label: 'Give feedback' },
  { kind: 'help', label: 'Ask for help' },
];

/** What the pill read when its menu opened. */
export interface PillOrigin {
  pathname: string;
  search: string;
  /** the page's own screen id (`data-screen-id` on an ancestor), when it declares one */
  screenId: string | null;
  panelId: string | null;
}

/** The DOM a pill reads: an Element in the browser, a plain object in a test. */
export interface PanelNode {
  getAttribute(name: string): string | null;
  parentElement: PanelNode | null;
}

/**
 * The panel a pill sits on: the nearest element declaring `data-panel-id`, record presenter (record-pane / record-page),
 * titled card (card:<title id>) or page head — a page head names its page's `data-panel-id` when the page declares one,
 * else "page".
 */
export function panelOf(from: PanelNode | null): string | null {
  for (let el = from; el; el = el.parentElement) {
    const own = el.getAttribute('data-panel-id');
    if (own) return own;
    const presenter = el.getAttribute('data-presenter');
    if (presenter) return `record-${presenter}`;
    const card = el.getAttribute('data-kit') === 'card' ? el.getAttribute('aria-labelledby') : null;
    if (card) return `card:${card}`;
    if (el.getAttribute('data-testid') === 'page-head') {
      for (let up = el.parentElement; up; up = up.parentElement) if (up.getAttribute('data-panel-id')) return up.getAttribute('data-panel-id');
      return 'page';
    }
  }
  return null;
}

export function pillOrigin(from: PanelNode | null, at: { pathname: string; search: string }): PillOrigin {
  let screenId: string | null = null;
  for (let el = from; el && !screenId; el = el.parentElement) screenId = el.getAttribute('data-screen-id');
  return { pathname: at.pathname, search: at.search, screenId, panelId: panelOf(from) };
}

export interface FeedbackAbout {
  text: string;
  route: string;
  screen: string | null;
  panel: string | null;
}

export interface FeedbackValue {
  available: boolean;
  /** Open the form for `kind`; `recordKey` is the record the panel shows; `origin` what the pill read when it opened. */
  open: (kind: FeedbackKind, panel: { recordKey?: string | null; origin?: PillOrigin }) => void;
  /** The About line for what the pill read (the shell adds the app, the registered screen and the build). */
  describe?: (origin: PillOrigin, recordKey: string | null) => FeedbackAbout;
}

const Ctx = React.createContext<FeedbackValue>({ available: false, open: () => {} });
export const FeedbackProvider = Ctx.Provider;

/** Mounted when the menu opens (the popup is unmounted while closed): reads the origin once, there and then. */
function About({ read, recordKey }: { read: () => PillOrigin; recordKey: string | null }) {
  const f = React.useContext(Ctx);
  const [origin] = React.useState(read);
  const about = f.describe?.(origin, recordKey);
  if (!about) return null;
  return (
    <MenuText>
      <span data-testid="feedback-about" data-route={about.route} data-screen={about.screen ?? ''} data-panel={about.panel ?? ''}>
        About: {about.text}
      </span>
    </MenuText>
  );
}

export function FeedbackPill({ recordKey }: { recordKey?: string | null }) {
  const f = React.useContext(Ctx);
  const at = React.useRef<HTMLSpanElement>(null);
  const origin = React.useRef<PillOrigin | null>(null);
  const read = React.useCallback(() => {
    origin.current = pillOrigin(at.current, { pathname: window.location.pathname, search: window.location.search });
    return origin.current;
  }, []);
  if (!f.available) return null;
  return (
    <span data-kit="feedback-pill" className="k-feedback-pill" ref={at}>
      <Menu
        aria-label="Feedback and help"
        align="end"
        trigger={
          <Button icon variant="quiet" aria-label="Feedback and help" data-testid="feedback-pill">
            <MoreHorizontal aria-hidden />
          </Button>
        }
      >
        <About read={read} recordKey={recordKey ?? null} />
        {FEEDBACK_KINDS.map((k) => (
          <MenuItem key={k.kind} data-testid={`feedback-${k.kind}`} onClick={() => f.open(k.kind, { recordKey: recordKey ?? null, origin: origin.current ?? read() })}>
            {k.label}
          </MenuItem>
        ))}
      </Menu>
    </span>
  );
}
